/**
 * Canonical Questionnaire — composition normalizer.
 *
 * Turns the Part 2 composed-questionnaire response into the read-only render
 * model the generic renderer walks. The response itself is treated as strictly
 * immutable input: nothing here writes to it, and every value handed onward is
 * either a fresh object or a deep clone.
 *
 * Wire shape (Part 2 `compose_questionnaire`):
 *   {
 *     organization_id, submission_id, membership_count,
 *     stages: [{
 *       entity_id, machine_key, title, sort_order,
 *       screens: [{
 *         entity_id, machine_key, title, stage_id, sort_order,
 *         placements: [{
 *           entity_id, canonical_question_id, sort_order, visible,
 *           widget_type, widget_override, answer_options, title,
 *           display_label, help_text, machine_key,
 *           canonical: { ...same display fields..., answer_type },
 *           shape_prefill: { value, evidence, element_key, source } | null,
 *           answer: { value, source, canonical_question_id, submission_id } | omitted
 *             // present only when a canonical_answer row exists (incl. clears)
 *         }]
 *       }]
 *     }]
 *   }
 *
 * ORDER: stages, screens and questions are emitted in exactly the order the API
 * returned them. The composition endpoint already sorts placements by
 * sort_order; re-sorting here would let the renderer disagree with the API.
 */
import { mapPlacementFromApi } from "@/helpers/cwbMappers";
import { unwrapAnswerValue } from "./answerResolution";

/** The render model for "no composition" — safe to walk, renders nothing. */
export const EMPTY_COMPOSITION = Object.freeze({
  organizationId: null,
  submissionId: null,
  stages: Object.freeze([]),
});

function isPlainObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * Deep clone so a widget can never reach back into the composition response.
 * `structuredClone` is the codebase's clone of choice; if cloning fails we
 * degrade to identity so rendering continues rather than throwing.
 */
export function cloneValue(value) {
  if (value === null || typeof value !== "object") return value;
  try {
    return structuredClone(value);
  } catch {
    return value;
  }
}

/**
 * A question's stable Vue key.
 *
 * Placement id first: it is the identity of THIS question ON THIS SCREEN, and it
 * is the only one of the three guaranteed unique within a composition. One
 * canonical question may be placed more than once (the same question asked on
 * two screens, or twice on one screen), and duplicate `:key`s make Vue reuse the
 * wrong component instance — so keying on the canonical id first would let two
 * placements fight over one input's in-progress state.
 *
 * Canonical-question id and machine key remain fallbacks for partially-populated
 * rows, then a positional key so a row with no identity at all still renders.
 *
 * NOTE this is the render key only. Emitted events continue to identify a
 * question by `canonicalQuestionId` + `machineKey`, which is what the rest of the
 * system stores answers against.
 */
function questionKey(question, index) {
  return question.placementId || question.canonicalQuestionId || question.machineKey || `question-${index}`;
}

function normalizeQuestion(rawPlacement, index) {
  if (!isPlainObject(rawPlacement)) return null;

  // Reuse the existing Part 1 placement mapper so nested/flattened/legacy
  // field names are handled in exactly one place (helpers/cwbMappers.js).
  const mapped = mapPlacementFromApi(rawPlacement);
  const canonical = rawPlacement.canonical || rawPlacement.canonical_question || {};

  const prefill = isPlainObject(rawPlacement.shape_prefill) ? rawPlacement.shape_prefill : null;
  const hasPersistedAnswer = Object.hasOwn(rawPlacement, "answer");
  const answerEnvelope = hasPersistedAnswer ? rawPlacement.answer : undefined;

  const question = {
    placementId: mapped.entity_id ?? null,
    canonicalQuestionId: mapped.canonical_question_id ?? null,
    machineKey: mapped.machine_key || "",
    title: mapped.title || "",
    displayLabel: mapped.display_label || "",
    helpText: mapped.help_text || "",
    answerOptions: asArray(mapped.answer_options).map(cloneValue),
    // A placement-level override beats the canonical question's widget.
    widgetType: mapped.widget_override || mapped.widget_type || canonical.widget_type || "",
    answerType: rawPlacement.answer_type ?? canonical.answer_type ?? "",
    sortOrder: mapped.sort_order ?? 0,
    visible: rawPlacement.visible !== false,
    conditional: rawPlacement.conditional == null ? null : cloneValue(rawPlacement.conditional),
    // Widget configuration travels with the question, not the renderer.
    widgetConfig: cloneValue(rawPlacement.widget_config ?? canonical.widget_config ?? null),
    prefillValue: prefill ? cloneValue(unwrapAnswerValue(prefill)) : undefined,
    prefillSource: prefill ? prefill.source || null : null,
    hasPersistedAnswer,
    persistedValue: hasPersistedAnswer
      ? cloneValue(unwrapAnswerValue(answerEnvelope))
      : undefined,
  };

  question.key = questionKey(question, index);
  return Object.freeze(question);
}

function normalizeScreen(rawScreen, index) {
  if (!isPlainObject(rawScreen)) return null;
  const entityId = rawScreen.entity_id ?? rawScreen.id ?? null;
  return Object.freeze({
    entityId,
    machineKey: rawScreen.machine_key ?? rawScreen.key ?? "",
    title: rawScreen.title ?? rawScreen.label ?? "",
    stageId: rawScreen.stage_id ?? null,
    sortOrder: rawScreen.sort_order ?? 0,
    key: entityId || rawScreen.machine_key || `screen-${index}`,
    questions: Object.freeze(asArray(rawScreen.placements).map(normalizeQuestion).filter(Boolean)),
  });
}

function normalizeStage(rawStage, index) {
  if (!isPlainObject(rawStage)) return null;
  const entityId = rawStage.entity_id ?? rawStage.id ?? null;
  return Object.freeze({
    entityId,
    machineKey: rawStage.machine_key ?? rawStage.key ?? "",
    title: rawStage.title ?? rawStage.label ?? "",
    sortOrder: rawStage.sort_order ?? 0,
    key: entityId || rawStage.machine_key || `stage-${index}`,
    screens: Object.freeze(asArray(rawStage.screens).map(normalizeScreen).filter(Boolean)),
  });
}

/**
 * Normalize a composition response into the render model.
 *
 * Missing, null, or malformed input yields {@link EMPTY_COMPOSITION} rather
 * than throwing, so a screen can render before its composition arrives.
 */
export function normalizeComposition(raw) {
  if (!isPlainObject(raw)) return EMPTY_COMPOSITION;
  return Object.freeze({
    organizationId: raw.organization_id ?? raw.organizationId ?? null,
    submissionId: raw.submission_id ?? raw.submissionId ?? null,
    stages: Object.freeze(asArray(raw.stages).map(normalizeStage).filter(Boolean)),
  });
}

/** Flat list of every normalized question, in composition order. */
export function collectQuestions(composition) {
  const model = composition?.stages ? composition : normalizeComposition(composition);
  const questions = [];
  for (const stage of model.stages) {
    for (const screen of stage.screens) {
      questions.push(...screen.questions);
    }
  }
  return questions;
}
